"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CoinCapMarketService = void 0;
const caip_1 = require("@shapeshiftoss/caip");
const types_1 = require("@shapeshiftoss/types");
const dayjs_1 = __importDefault(require("dayjs"));
const omit_1 = __importDefault(require("lodash/omit"));
const config_1 = require("../config");
const bignumber_1 = require("../utils/bignumber");
const isValidDate_1 = require("../utils/isValidDate");
const rateLimiters_1 = require("../utils/rateLimiters");
const axios = (0, rateLimiters_1.rateLimitedAxios)(config_1.RATE_LIMIT_THRESHOLDS_PER_MINUTE.COINCAP);
class CoinCapMarketService {
    constructor() {
        this.baseUrl = 'https://api.coincap.io/v2';
        this.defaultGetByMarketCapArgs = {
            count: 2500
        };
        this.findAll = async (args) => {
            const argsToUse = { ...this.defaultGetByMarketCapArgs, ...args };
            const { count } = argsToUse;
            const perPage = count > 250 ? 250 : count;
            const pages = Math.ceil((0, bignumber_1.bnOrZero)(count).div(perPage).toNumber());
            const urlAtPage = (page) => `${this.baseUrl}/assets?limit=${perPage}&offset=${page}`;
            const pageCount = Array(pages)
                .fill(0)
                .map((_v, i) => i + 1);
            try {
                const combined = (await Promise.all(pageCount.map(async (page) => axios.get(urlAtPage(page))))).flat();
                return combined
                    .map(({ data }) => (data && data.data ? data.data : [])) // filter out rate limited results
                    .flat()
                    .sort((a, b) => (a.rank > b.rank ? 1 : -1))
                    .reduce((acc, cur) => {
                    const { id } = cur;
                    try {
                        const assetId = caip_1.adapters.coincapToAssetId(id);
                        if (!assetId)
                            return acc;
                        const curWithoutId = (0, omit_1.default)(cur, 'id'); // don't leak this through to clients
                        acc[assetId] = {
                            price: curWithoutId.priceUsd.toString(),
                            marketCap: curWithoutId.marketCapUsd.toString(),
                            volume: curWithoutId.volumeUsd24Hr.toString(),
                            changePercent24Hr: parseFloat(curWithoutId.changePercent24Hr)
                        };
                        return acc;
                    }
                    catch (_a) {
                        return acc; // no AssetId found, we don't support this asset
                    }
                }, {});
            }
            catch (e) {
                return {};
            }
        };
        this.findByAssetId = async ({ assetId }) => {
            if (!caip_1.adapters.assetIdToCoinCap(assetId))
                return null;
            try {
                const id = caip_1.adapters.assetIdToCoinCap(assetId);
                const { data } = await axios.get(`${this.baseUrl}/assets/${id}`);
                const marketData = data.data;
                return {
                    price: marketData.priceUsd,
                    marketCap: marketData.marketCapUsd,
                    changePercent24Hr: parseFloat(marketData.changePercent24Hr),
                    volume: marketData.volumeUsd24Hr
                };
            }
            catch (e) {
                console.warn(e);
                throw new Error('MarketService(findByAssetId): error fetching market data');
            }
        };
        this.findPriceHistoryByAssetId = async ({ assetId, timeframe }) => {
            if (!caip_1.adapters.assetIdToCoinCap(assetId))
                return [];
            const id = caip_1.adapters.assetIdToCoinCap(assetId);
            const end = (0, dayjs_1.default)().startOf('minute');
            let start;
            let interval;
            switch (timeframe) {
                case types_1.HistoryTimeframe.HOUR:
                    start = end.subtract(1, 'hour');
                    interval = 'm5';
                    break;
                case types_1.HistoryTimeframe.DAY:
                    start = end.subtract(1, 'day');
                    interval = 'h1';
                    break;
                case types_1.HistoryTimeframe.WEEK:
                    start = end.subtract(1, 'week');
                    interval = 'd1';
                    break;
                case types_1.HistoryTimeframe.MONTH:
                    start = end.subtract(1, 'month');
                    interval = 'd1';
                    break;
                case types_1.HistoryTimeframe.YEAR:
                    start = end.subtract(1, 'year');
                    interval = 'd1';
                    break;
                case types_1.HistoryTimeframe.ALL:
                    start = end.subtract(20, 'years');
                    interval = 'd1';
                    break;
                default:
                    start = end;
            }
            try {
                const from = start.valueOf();
                const to = end.valueOf();
                const url = `${this.baseUrl}/assets/${id}/history`;
                const { data: { data: coincapData } } = await axios.get(`${url}?id=${id}&start=${from}&end=${to}&interval=${interval}`);
                return coincapData.reduce((acc, current) => {
                    const date = current.time;
                    if (!(0, isValidDate_1.isValidDate)(date)) {
                        console.error('Coincap asset history data has invalid date');
                        return acc;
                    }
                    const price = (0, bignumber_1.bn)(current.priceUsd);
                    if (price.isNaN()) {
                        console.error('Coincap asset history data has invalid price');
                        return acc;
                    }
                    acc.push({
                        date,
                        price: price.toNumber()
                    });
                    return acc;
                }, []);
            }
            catch (e) {
                console.warn(e);
                throw new Error('MarketService(findPriceHistoryByAssetId): error fetching price history');
            }
        };
    }
}
exports.CoinCapMarketService = CoinCapMarketService;
